import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";

import {
  Box,
  Typography,
  Card,
  Button,
  Avatar,
  useTheme,
  Paper,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Badge,
  Fade,
  Tooltip,
} from "@mui/material";
import {
  Warning,
  StickyNote2 as NoteIcon,
  Visibility,
  VisibilityOff,
  AccessTime,
  Schedule,
  Timer,
  KeyboardArrowUp,
  KeyboardArrowDown,
} from "@mui/icons-material";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import PersonIcon from "@mui/icons-material/Person";
import MockNoteCompletion from "./MockNoteCompletion";
import MockMcq from "./MockMcq";
import TextHighlighterWrapper from "./TextHighlighterWrapper";
import NotesModal from "./NotesModal";
import HtmlRenderer from "./HtmlRenderer";

/* ---------------------------------------------------------------
   Utils
--------------------------------------------------------------- */

const BASE_URL = "https://api.mmitsol.com/";

const pad2 = (n) => String(n).padStart(2, "0");
const fmtClock = (s) => {
  if (s == null) return "--:--:--";
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  return `${pad2(h)}:${pad2(m)}:${pad2(sec)}`;
};

function normalizeHeading(raw) {
  if (!raw) return "";
  const parts = String(raw)
    .split(/\r?\n+/)
    .map((s) => s.trim())
    .filter(Boolean);
  const joined = parts.join(" – ");
  return joined.replace(/\s{2,}/g, " ").trim();
}

function cleanQuestionText(raw = "") {
  return String(raw).replace(/\s+/g, " ").trim();
}

const hasAnswer = (answers, id) =>
  id != null && String(answers?.[id] ?? "").trim() !== "";

/* ---------------------------------------------------------------
   Data Normalizers for Playlist Construction
--------------------------------------------------------------- */

function normalizeLegacyPartAExtract(extract) {
  // Case 1: aggregated sections wrapper (current parent output)
  const maybeSections = extract?.questions?.[0]?.content?.sections;
  if (Array.isArray(maybeSections) && maybeSections.length) {
    const sections = maybeSections.map((sec) => {
      const notes = (sec?.notes || []).map((n) => {
        const qid = n?.questionId ?? n?.question_id ?? null;
        const rawTxt = n?.text ?? n?.question ?? "";
        const txt = cleanQuestionText(rawTxt);
        const inferBlank =
          qid != null || /\(\d+\)/.test(rawTxt) || /_{2,}/.test(rawTxt);
        const hasBlank = n?.hasBlank != null ? !!n.hasBlank : inferBlank;
        return {
          questionId: qid,
          text: txt,
          hasBlank,
        };
      });
      return {
        heading: normalizeHeading(sec?.heading),
        notes,
      };
    });

    const questionIds = [];
    sections.forEach((s) =>
      s.notes.forEach((n) => {
        if (n.questionId != null) questionIds.push(Number(n.questionId));
      })
    );

    // Patient name from Patient section first note that has no blank
    let patientName = "";
    const patientSec = sections.find((s) => /^patient\b/i.test(s.heading));
    if (patientSec?.notes?.length) {
      const nameNote =
        patientSec.notes.find((n) => !n.hasBlank && n.text?.trim()) ||
        patientSec.notes.find((n) => n.text?.trim()) ||
        patientSec.notes[0];
      patientName = (nameNote?.text || "").trim();
    }

    return { sections, questionIds, patientName };
  }

  // Case 2: per-question content array fallback
  const qArr = extract?.questions?.map((q) => q?.content) || [];
  const byHeading = new Map();
  qArr.forEach((n) => {
    const heading = normalizeHeading(n?.heading);
    if (!byHeading.has(heading)) byHeading.set(heading, []);
    byHeading.get(heading).push({
      questionId: n?.questionId ?? null,
      text: cleanQuestionText(n?.question ?? ""),
      hasBlank: n?.questionId != null,
    });
  });
  const sections = Array.from(byHeading.entries()).map(([heading, notes]) => ({
    heading,
    notes,
  }));
  const questionIds = qArr
    .map((n) => n?.questionId)
    .filter((v) => v != null)
    .map(Number);
  let patientName = "";
  const patientSec = sections.find((s) => /^patient\b/i.test(s.heading));
  if (patientSec?.notes?.length) {
    patientName = patientSec.notes[0].text;
  }
  return { sections, questionIds, patientName };
}

function normalizeLegacyPartBExtract(extract) {
  return (
    extract?.questions?.map((qWrap, idx) => {
      const q = qWrap?.content || {};
      return {
        part: "B",
        type: "mcqSingle",
        audioURL: extract?.audioURL || null,
        extractName:
          extract?.extractName ||
          `Extract ${idx + 1} – Question ${q?.questionId ?? idx + 1}`,
        instructions: extract?.instructions || "",
        content: {
          questionId: q?.questionId,
          question_id: q?.question_id,
          question: q?.question || "",
          options: q?.options || [],
        },
      };
    }) || []
  );
}

function normalizeLegacyPartCExtract(extract) {
  const contents =
    extract?.questions?.map((qWrap) => {
      const q = qWrap?.content || {};
      return {
        questionId: q?.questionId,
        question_id: q?.question_id,
        question: q?.question || "",
        options: q?.options || [],
      };
    }) || [];
  const questionIds = contents
    .map((c) => c?.questionId)
    .filter((v) => v != null)
    .map(Number);
  return {
    part: "C",
    type: "mcqGroup",
    audioURL: extract?.audioURL || null,
    extractName: extract?.extractName || "",
    instructions: extract?.instructions || "",
    contents,
    questionIds,
  };
}

function buildPlaylist(listeningData = {}) {
  const playlist = [];

  // Handle new API format with listeningSections (direct questions array)
  if (
    listeningData?.listeningSections &&
    Array.isArray(listeningData.listeningSections)
  ) {
    listeningData.listeningSections.forEach((section, sectionIndex) => {
      const questions = section.questions || [];

      // Group questions by their explicit part (question.part) when available,
      // otherwise fall back to section.part or default to A.
      const byPart = { A: [], B: [], C: [] };
      questions.forEach((q) => {
        const p = (q.part || section.part || "A").toUpperCase();
        if (p === "B") byPart.B.push(q);
        else if (p === "C") byPart.C.push(q);
        else byPart.A.push(q);
      });

      // If the section contains mixed parts (A + B/C), create a composite screen
      // that shows Part A note completion followed by any Part B single MCQs
      // and Part C grouped MCQs. This keeps the UI on a single page per section
      // which matches the desired layout for the newer API shape.
      const hasA = byPart.A.length > 0;
      const hasB = byPart.B.length > 0;
      const hasC = byPart.C.length > 0;

      if (hasA || hasB || hasC) {
        const questionIds = [];

        // Extract instructions from first question (all questions in section share same instructions)
        const sectionInstructions =
          questions[0]?.instructions || section.instructions || "";

        // Build an ordered items array from original questions so we preserve sequence
        const items = questions.map((question) => {
          const qid = question.questionId ?? question.question_id ?? null;
          if (qid != null) questionIds.push(Number(qid));

          const qType = (
            question.questionType ||
            question.type ||
            ""
          ).toLowerCase();

          if (
            qType.includes("note") ||
            qType.includes("notes") ||
            qType.includes("completion") ||
            question.questionType === "notes_completion"
          ) {
            return {
              type: "note",
              questionId: qid,
              question: cleanQuestionText(question.question || ""),
              heading:
                question.questionHeading ||
                question.heading ||
                question.questionMainHeading ||
                "",
              questionMainHeading: question.questionMainHeading || "",
              instructions: question.instructions || "",
            };
          }

          if (
            qType.includes("mcq") ||
            qType.includes("multiple") ||
            qType.includes("single") ||
            question.options
          ) {
            const isMulti =
              /muti|multi|multiple/i.test(question.questionType || "") ||
              question.questionType === "mcq_mutiple";
            return {
              type: isMulti ? "mcq_mutiple" : "mcq_single",
              questionId: qid,
              question_id: qid,
              question: question.question || "",
              options: question.options || [],
              heading: question.questionHeading || question.heading || "",
              questionNumber: question.questionNumber || "",
              questionType:
                question.questionType ||
                (isMulti ? "mcq_mutiple" : "mcq_single"),
              instructions: question.instructions || "",
            };
          }

          // Fallback generic item
          return {
            type: question.questionType || "unknown",
            questionId: qid,
            question: question.question || "",
            heading: question.questionHeading || question.heading || "",
            options: question.options || [],
          };
        });

        playlist.push({
          part: section.part || (hasA ? "A" : hasB ? "B" : "C"),
          type: "composite",
          audioURL: section.audioUrl || null,
          extractName: section.title || `Section ${sectionIndex + 1}`,
          instructions: sectionInstructions,
          content: {
            items,
          },
          questionIds,
        });
      }
    });

    return playlist;
  }

  // Handle new API format with listeningParts
  if (
    listeningData?.listeningParts &&
    Array.isArray(listeningData.listeningParts)
  ) {
    listeningData.listeningParts.forEach((part) => {
      const partType = part.part?.toUpperCase();

      if (partType === "A" && part.listeningExtract) {
        // Part A: Note completion - group all questions by extract
        part.listeningExtract.forEach((extract, extractIndex) => {
          if (extract.extractQuestion && extract.extractQuestion.length > 0) {
            // Group questions by heading to create sections
            const byHeading = new Map();
            const questionIds = [];

            extract.extractQuestion.forEach((question) => {
              const heading = normalizeHeading(question.heading);
              if (!byHeading.has(heading)) byHeading.set(heading, []);

              const qid = question.questionId;
              const rawTxt = question.question || "";
              const txt = cleanQuestionText(rawTxt);
              const inferBlank =
                qid != null || /\(\d+\)/.test(rawTxt) || /_{2,}/.test(rawTxt);
              const hasBlank = inferBlank;

              byHeading.get(heading).push({
                questionId: qid,
                text: txt,
                hasBlank,
              });

              if (qid != null) {
                questionIds.push(Number(qid));
              }
            });

            const sections = Array.from(byHeading.entries()).map(
              ([heading, notes]) => ({
                heading,
                notes,
              })
            );

            // Find patient name from Patient section
            let patientName = "";
            const patientSec = sections.find((s) =>
              /^patient\b/i.test(s.heading)
            );
            if (patientSec?.notes?.length) {
              const nameNote =
                patientSec.notes.find((n) => !n.hasBlank && n.text?.trim()) ||
                patientSec.notes.find((n) => n.text?.trim()) ||
                patientSec.notes[0];
              patientName = (nameNote?.text || "").trim();
            }

            playlist.push({
              part: "A",
              type: "noteCompletion",
              audioURL: extract.audioUrl || null,
              extractName: extract.extractName || `Extract ${extractIndex + 1}`,
              instructions: extract.instructions || "",
              content: { sections },
              questionIds,
              patientName,
            });
          }
        });
      }

      if (partType === "B" && part.listeningExtract) {
        // Part B: Each extract has one question - create separate screens
        part.listeningExtract.forEach((extract, extractIndex) => {
          if (extract.extractQuestion && extract.extractQuestion.length > 0) {
            extract.extractQuestion.forEach((question, questionIndex) => {
              playlist.push({
                part: "B",
                type: "mcqSingle",
                audioURL: extract.audioUrl || null,
                extractName:
                  extract.extractName || `Extract ${extractIndex + 1}`,
                instructions: extract.instructions || "",
                content: {
                  questionId: question.questionId,
                  question_id: question.questionId,
                  question: question.question || "",
                  options: question.options || [],
                  heading: question.heading || "",
                  questionNumber: question.questionNumber || "",
                  questionType: question.questionType || "mcq",
                },
              });
            });
          }
        });
      }

      if (partType === "C" && part.listeningExtract) {
        // Part C: Multiple choice group - group all questions per extract
        part.listeningExtract.forEach((extract, extractIndex) => {
          if (extract.extractQuestion && extract.extractQuestion.length > 0) {
            const contents = extract.extractQuestion.map((question) => ({
              questionId: question.questionId,
              question_id: question.questionId,
              question: question.question || "",
              options: question.options || [],
              heading: question.heading || "",
              questionNumber: question.questionNumber || "",
              questionType: question.questionType || "mcq",
            }));

            const questionIds = contents
              .map((c) => c?.questionId)
              .filter((v) => v != null)
              .map(Number);

            playlist.push({
              part: "C",
              type: "mcqGroup",
              audioURL: extract.audioUrl || null,
              extractName: extract.extractName || `Extract ${extractIndex + 1}`,
              instructions: extract.instructions || "",
              contents,
              questionIds,
            });
          }
        });
      }
    });

    return playlist;
  }

  // Fallback to legacy format
  const partA = listeningData?.partA;
  const partB = listeningData?.partB;
  const partC = listeningData?.partC;

  // Part A
  partA?.extracts?.forEach((ex) => {
    const norm = normalizeLegacyPartAExtract(ex);
    playlist.push({
      part: "A",
      type: "noteCompletion",
      audioURL: ex?.audioURL || null,
      extractName: ex?.extractName || cleanQuestionText(ex.instructions),
      instructions: ex?.instructions || "",
      content: { sections: norm.sections },
      questionIds: norm.questionIds,
      patientName: norm.patientName,
    });
  });

  // Part B
  partB?.extracts?.forEach((ex) => {
    normalizeLegacyPartBExtract(ex).forEach((screen) => playlist.push(screen));
  });

  // Part C
  partC?.extracts?.forEach((ex) => {
    playlist.push(normalizeLegacyPartCExtract(ex));
  });

  return playlist;
}

/* ---------------------------------------------------------------
   Header helpers
--------------------------------------------------------------- */

function getHeaderQuestionIds(screen, playlist) {
  if (!screen) return [];
  if (screen.part === "A") return screen.questionIds || [];
  if (screen.part === "B") {
    // For Part B, return all Part B question IDs for chip display
    const allPartBQuestions = playlist
      .filter((item) => item.part === "B")
      .map((item) => {
        const id = item.content?.questionId ?? item.content?.question_id;
        return id != null ? Number(id) : null;
      })
      .filter((id) => id != null);

    return allPartBQuestions;
  }
  if (screen.part === "C") return screen.questionIds || [];
  return [];
}

function getExtractProgress(screen, playlist) {
  if (!screen) return { idx: 0, total: 0 };
  const part = screen.part;
  const inPart = playlist.filter((p) => p.part === part);
  const globalIdx = playlist.indexOf(screen);
  const idx = inPart.findIndex((p) => playlist.indexOf(p) === globalIdx);
  return { idx: idx >= 0 ? idx : 0, total: inPart.length };
}

/* ---------------------------------------------------------------
   ListeningSection Component
--------------------------------------------------------------- */

export default function ListeningSection({
  listeningData,
  listenScreen,
  setListenScreen,
  audioRef,
  instrOpen,
  answers,
  onAnswerChange,
  onAutoNext,
  timer,
  mode = "simulation",
  currentPart = "A",
  startTime = null,
  endTime = null,
  onBack = null,
  isLastQuestion = false,
  isLastModuleInSimulation = false, // Whether this is the last module in simulation mode (no reading/writing/speaking after)
  isLastQuestionInSimulation = false, // Whether this is the last question/part in simulation mode
  onFinish = null,
  testId = null, // Test ID for browser back navigation
  // Notes props - now managed by parent component
  notes = [],
  onAddNote = null,
  notesModalOpen = false,
  setNotesModalOpen = null,
}) {
  // Navigation hook
  const navigate = useNavigate();

  // Handle browser back button - navigate to main screen
  useEffect(() => {
    if (!testId) return; // Only if testId is provided

    const handlePopState = (event) => {
      // Prevent default back navigation
      event.preventDefault();
      // Navigate to main mock test screen using React Router
      navigate(`/mock-test/${testId}`, { replace: true });
    };

    // Add popstate event listener
    window.addEventListener("popstate", handlePopState);

    // Push current state to allow back button to trigger our handler
    window.history.pushState(null, "", window.location.pathname);

    // Cleanup
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [testId, navigate]);

  // Track previous instrOpen to detect close event
  const prevInstrOpenRef = useRef(instrOpen);

  // Pause and resume audio when instruction modal opens/closes
  useEffect(() => {
    const audio = audioRef?.current;
    if (!audio) return;

    if (instrOpen) {
      audio.pause();
    } else if (prevInstrOpenRef.current && !instrOpen) {
      setTimeout(() => {
        if (audio && !audio.ended) {
          audio.play().catch(() => {});
        }
      }, 100);
    }
    prevInstrOpenRef.current = instrOpen;
  }, [mode, instrOpen, audioRef]);

  // Ensure audio elements are synchronized between desktop and mobile views
  useEffect(() => {
    // Find all audio elements and ensure the visible one is referenced
    const handleResize = () => {
      const desktopAudio = document.querySelector(
        'audio[data-audio-type="desktop"]'
      );
      const mobileAudio = document.querySelector(
        'audio[data-audio-type="mobile"]'
      );

      if (
        window.innerWidth >= 960 &&
        desktopAudio &&
        desktopAudio.style.display !== "none"
      ) {
        audioRef.current = desktopAudio;
      } else if (
        window.innerWidth < 960 &&
        mobileAudio &&
        mobileAudio.style.display !== "none"
      ) {
        audioRef.current = mobileAudio;
      }
    };

    handleResize(); // Initial assignment
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, [audioRef]);

  const theme = useTheme();
  const divider = theme.palette.divider;
  const bgPaper = theme.palette.background.paper;
  const textPrimary = theme.palette.text.primary;
  const border = theme.palette.divider;

  /* Build playlist from listening data */
  const playlist = useMemo(() => {
    console.log(
      "[DEBUG ListeningSection] Building playlist from listeningData:",
      listeningData
    );
    // If listeningData has partA/partB/partC (transformed format), use it directly
    // Otherwise, try to build from raw format
    if (listeningData?.partA || listeningData?.partB || listeningData?.partC) {
      console.log(
        "[DEBUG ListeningSection] Using transformed format (partA/B/C)"
      );
      return buildPlaylist(listeningData);
    }
    // If it's raw API format with listeningSections, build playlist from it
    if (listeningData?.listeningSections) {
      console.log(
        "[DEBUG ListeningSection] Using raw API format (listeningSections)"
      );
      console.log(
        "[DEBUG ListeningSection] listeningSections:",
        listeningData.listeningSections
      );
      const result = buildPlaylist(listeningData);
      console.log("[DEBUG ListeningSection] Playlist built:", result);
      return result;
    }
    console.log(
      "[DEBUG ListeningSection] No valid format found, returning empty playlist"
    );
    return [];
  }, [listeningData]);
  const screen = playlist[listenScreen] || null;
  console.log(
    "[DEBUG ListeningSection] Current screen (listenScreen=" +
      listenScreen +
      "):",
    screen
  );

  // Scroll to top when screen changes (moving to next extract/part)
  useEffect(() => {
    const mainContent = document.querySelector(".listening-main-content");
    if (mainContent) {
      mainContent.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }, [listenScreen]);

  // Part A: filter out the "Patient" section; build summary bullets
  const partAFiltered = useMemo(() => {
    if (!screen || screen.part !== "A") {
      console.log("[DEBUG partAFiltered] Not Part A or no screen");
      return { sections: [], summary: null, questionIds: [] };
    }
    const allSecs = screen.content?.sections || [];
    console.log("[DEBUG partAFiltered] screen.content:", screen.content);
    console.log("[DEBUG partAFiltered] allSecs:", allSecs);

    const filteredSecs = allSecs.filter((s) => {
      const heading = (s.heading || "").trim();
      // Only filter out sections that are exactly "Patient" (case insensitive)
      return !/^patient$/i.test(heading);
    });

    console.log("[DEBUG partAFiltered] filteredSecs:", filteredSecs);

    // Collect all question IDs from filtered sections
    const questionIds = [];
    filteredSecs.forEach((sec) => {
      sec.notes?.forEach((note) => {
        if (note.questionId != null) {
          questionIds.push(Number(note.questionId));
        }
      });
    });

    // Summary: use the first non-Patient section
    const firstSec = filteredSecs[0];
    let summary = null;
    if (firstSec) {
      const staticLines = (firstSec.notes || [])
        .filter((n) => n.questionId == null && n.text?.trim())
        .slice(0, 2)
        .map((n) => n.text.trim());
      summary = {
        heading: firstSec.heading,
        lines: staticLines,
      };
    }
    console.log("[DEBUG partAFiltered] Final result:", {
      sections: filteredSecs,
      summary,
      questionIds,
    });
    return { sections: filteredSecs, summary, questionIds };
  }, [screen]);

  // Extract progress pill
  const { idx: partIdx, total: partTotal } = useMemo(
    () => getExtractProgress(screen, playlist),
    [screen, playlist]
  );

  // Question chips
  const headerQuestionIds = useMemo(
    () => getHeaderQuestionIds(screen, playlist),
    [screen, playlist]
  );

  // Audio timers
  const [audioDur, setAudioDur] = useState(0);
  const [audioPos, setAudioPos] = useState(0);

  // Practice timer state for both API-based and audio-based timing
  const [apiTimeRemaining, setApiTimeRemaining] = useState(0);
  const [audioTimeRemaining, setAudioTimeRemaining] = useState(0);
  const [timerPaused, setTimerPaused] = useState(false); // Start running, pause only when audio is paused
  const [audioStarted, setAudioStarted] = useState(false); // Track if audio has started playing
  const [currentTime, setCurrentTime] = useState(Date.now()); // For live timer updates

  // Timer expired modal state
  const [timerExpired, setTimerExpired] = useState(false);
  const [expiryCountdown, setExpiryCountdown] = useState(10);
  const [expiryHandled, setExpiryHandled] = useState(false);
  const [stayHereUsed, setStayHereUsed] = useState(false);
  const [timeExtension, setTimeExtension] = useState(0); // Track extended time in seconds

  // Extended timer for "Stay Here" functionality
  const [showExtendedTimer, setShowExtendedTimer] = useState(false);
  const [extendedCountdown, setExtendedCountdown] = useState(10);

  // Navigation confirmation dialog state
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  // Mouse position tracking for auto-hide header
  // Add a ref to hold the auto-advance timeout
  const autoAdvanceTimeoutRef = useRef(null);
  const extendedTimerRef = useRef(null);

  // Wrapper to track all setListenScreen calls
  const trackedSetListenScreen = useCallback(
    (value) => {
      console.log(
        "🚨 setListenScreen called with:",
        value,
        "Current screen:",
        listenScreen
      );
      console.trace("Call stack:");
      if (typeof value === "function") {
        setListenScreen((prev) => {
          const newValue = value(prev);
          console.log("🚨 setListenScreen changing from", prev, "to", newValue);
          return newValue;
        });
      } else {
        console.log("🚨 setListenScreen changing to", value);
        setListenScreen(value);
      }
    },
    [listenScreen, setListenScreen]
  );

  // Handle timer expiry - in practice mode, do nothing; in simulation, move as before
  const handleTimerExpire = useCallback(() => {
    console.log(
      "handleTimerExpire called - expiryHandled:",
      expiryHandled,
      "showExtendedTimer:",
      showExtendedTimer
    );

    if (!expiryHandled) {
      console.log("Processing timer expiry...");
      setExpiryHandled(true);
      setTimerExpired(false);

      // Practice mode: don't do anything automatically when timer expires
      // User must click Next button manually
      if (mode === "practice") {
        console.log("Practice mode - not auto-navigating");
        // Don't automatically finish or navigate - let user click Next
      } else {
        console.log("Simulation mode - auto-navigating...");
        // Simulation mode: auto-navigate as before
        // Check if there's a next extract in the current part
        if (listenScreen < playlist.length - 1) {
          console.log("Moving to next extract via handleTimerExpire");
          // Move to next extract
          trackedSetListenScreen((prev) => prev + 1);
        } else if (isLastQuestion && onFinish) {
          console.log("Finishing test via handleTimerExpire");
          // This was the last question overall
          onFinish();
        } else if (onAutoNext) {
          console.log("Moving to next part via handleTimerExpire");
          // Move to next part
          onAutoNext();
        }
      }
    } else {
      console.log("Timer expiry ignored - already handled");
    }
  }, [
    expiryHandled,
    mode,
    isLastQuestion,
    onFinish,
    onAutoNext,
    listenScreen,
    playlist.length,
    setListenScreen,
  ]);

  // Timer effect for API-based timing (startTime/endTime) - only if endTime is provided
  useEffect(() => {
    if (!endTime) return;

    // Don't start timer if instructions are currently open
    if (instrOpen) {
      return;
    }

    const updateApiTimer = () => {
      if (timerPaused) return;

      const now = new Date();
      const endDateTime = new Date(endTime);
      // Add time extension if "Stay Here" was used
      const adjustedEndTime = endDateTime.getTime() + timeExtension * 1000;
      const remainingMs = adjustedEndTime - now.getTime();
      const remainingSeconds = Math.max(0, Math.floor(remainingMs / 1000));

      setApiTimeRemaining(remainingSeconds);

      // Check for timer expiration and show modal
      if (remainingSeconds <= 0 && !timerExpired && !expiryHandled) {
        if (stayHereUsed) {
          // If "Stay Here" was already used, auto-advance without modal
          setExpiryHandled(true);
          handleTimerExpire();
        } else {
          // Show modal with "Stay Here" option
          setTimerExpired(true);
          setExpiryHandled(false);
        }
      }
    };

    updateApiTimer();
    const interval = setInterval(updateApiTimer, 1000);

    const handlePauseTimer = () => setTimerPaused(true);
    window.addEventListener("pauseTestTimers", handlePauseTimer);

    return () => {
      clearInterval(interval);
      window.removeEventListener("pauseTestTimers", handlePauseTimer);
    };
  }, [
    endTime,
    timerPaused,
    instrOpen,
    timerExpired,
    expiryHandled,
    timeExtension,
    stayHereUsed,
    handleTimerExpire,
  ]); // Added timeExtension and stayHereUsed

  // Timer effect for audio-based timing (using audio duration) - only if no endTime
  useEffect(() => {
    if (endTime) return; // Skip if API timer is being used

    // Don't start timer if instructions are currently open
    if (instrOpen) {
      return;
    }

    // For practice mode, only start if audio has started playing
    if (mode === "practice" && !audioStarted) {
      return;
    }

    // Only start timer if we have audio duration
    if (audioDur <= 0) {
      return;
    }

    const updateAudioTimer = () => {
      if (timerPaused) {
        return;
      }

      // Get audio reference and make sure it's available
      const audio = audioRef?.current;
      if (!audio) {
        return;
      }

      // Get the latest audio position directly from the audio element
      const currentPos = audio.currentTime || 0;
      // Add time extension if "Stay Here" was used
      const adjustedDuration = audioDur + timeExtension;
      const remainingSeconds = Math.max(
        0,
        Math.floor(adjustedDuration - currentPos)
      );

      // In practice mode, don't update audioTimeRemaining here - let the timeupdate event handle it
      // This prevents conflicts between the interval timer and audio events
      if (mode !== "practice") {
        // Only update for simulation mode via this interval
        setAudioTimeRemaining(remainingSeconds);
      }

      // Check for timer expiration
      if (remainingSeconds <= 0 && !timerExpired && !expiryHandled) {
        if (mode === "simulation") {
          if (stayHereUsed) {
            // If "Stay Here" was already used, auto-advance without modal
            setExpiryHandled(true);
            handleTimerExpire();
          } else {
            // Show modal with "Stay Here" option
            setTimerExpired(true);
            setExpiryHandled(false);
          }
        } else {
          // In practice mode, just mark as expired without showing modal
          setExpiryHandled(true);
        }
      }
    };

    updateAudioTimer();
    const interval = setInterval(updateAudioTimer, 1000);

    const handlePauseTimer = () => setTimerPaused(true);
    window.addEventListener("pauseTestTimers", handlePauseTimer);

    return () => {
      clearInterval(interval);
      window.removeEventListener("pauseTestTimers", handlePauseTimer);
    };
  }, [
    audioDur,
    audioPos,
    timerPaused,
    instrOpen,
    timerExpired,
    expiryHandled,
    mode,
    audioStarted,
    timeExtension,
    stayHereUsed,
    handleTimerExpire,
  ]); // Added timeExtension, stayHereUsed, and handleTimerExpire

  // Timer expiry effect: start countdown and auto-advance unless user clicks Continue
  useEffect(() => {
    if (timerExpired && !expiryHandled) {
      setExpiryCountdown(10);
      const interval = setInterval(() => {
        setExpiryCountdown((s) => {
          if (s <= 1) {
            clearInterval(interval);
            handleTimerExpire();
            return 10;
          }
          return s - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
    // If timerExpired becomes false (e.g., user clicked "Stay Here"), make sure countdown stops
  }, [timerExpired, expiryHandled, handleTimerExpire]);

  // "Continue" button handler for timer modal
  const handleTimerContinue = () => {
    if (!expiryHandled) {
      handleTimerExpire();
    }
  };

  // "Stay Here" button handler for timer modal
  const handleStayHere = useCallback(() => {
    console.log("Stay Here clicked - starting 10 second timer");

    // Close the modal and prevent all other timer logic
    setTimerExpired(false);
    setExpiryHandled(true); // This should block other timers
    setShowExtendedTimer(true);
    setExtendedCountdown(10);

    console.log("Modal closed, timer states set");

    // Clear any existing timers
    if (autoAdvanceTimeoutRef.current) {
      console.log("Clearing existing autoAdvanceTimeout");
      clearTimeout(autoAdvanceTimeoutRef.current);
      autoAdvanceTimeoutRef.current = null;
    }
    if (extendedTimerRef.current) {
      console.log("Clearing existing extendedTimer");
      clearInterval(extendedTimerRef.current);
      extendedTimerRef.current = null;
    }

    console.log("Starting extended countdown...");

    // Start countdown
    let count = 10;
    extendedTimerRef.current = setInterval(() => {
      count--;
      setExtendedCountdown(count);
      console.log("Extended countdown:", count);

      if (count <= 0) {
        console.log("Countdown finished - cleaning up and navigating");
        clearInterval(extendedTimerRef.current);
        extendedTimerRef.current = null;
        setShowExtendedTimer(false);
        setExpiryHandled(false); // Reset for next time

        console.log("Extended timer finished - moving to next");

        // Simple direct navigation
        if (mode === "simulation" && listenScreen < playlist.length - 1) {
          console.log("Navigating from", listenScreen, "to", listenScreen + 1);
          trackedSetListenScreen((prev) => prev + 1);
        }
      }
    }, 1000);

    console.log("Extended timer setup complete");
  }, [mode, listenScreen, playlist.length, trackedSetListenScreen]);

  // Reset expiryHandled for new extract (not just new part)
  useEffect(() => {
    setExpiryHandled(false);
    setTimerExpired(false);
    setStayHereUsed(false);
    setTimeExtension(0);
    setShowExtendedTimer(false);
    setExtendedCountdown(10);

    // Clear extended timer if running
    if (extendedTimerRef.current) {
      clearInterval(extendedTimerRef.current);
    }
  }, [listenScreen]); // Changed from currentPart to listenScreen

  // Navigation confirmation handlers
  const handleNextClick = useCallback(() => {
    // For validation, use only current screen questions, not all Part B questions
    let validationIds;
    if (screen?.part === "B") {
      // For Part B, only validate the current question
      const currentId =
        screen.content?.questionId ?? screen.content?.question_id;
      validationIds = currentId != null ? [Number(currentId)] : [];
    } else {
      // For Part A and C, use all questions on current screen
      validationIds = headerQuestionIds.filter(
        (id) => id != null && !isNaN(id) && typeof id === "number"
      );
    }

    // Check each ID for answers and provide detailed debugging
    const unattemptedDetails = validationIds.map((id) => {
      const answer = answers?.[id];
      const hasAnswerResult = hasAnswer(answers, id);
      const trimmedAnswer = String(answer ?? "").trim();
      return {
        id,
        answer,
        trimmedAnswer,
        hasAnswer: hasAnswerResult,
        isEmpty: trimmedAnswer === "",
      };
    });

    const unattemptedQuestions = unattemptedDetails.filter(
      (detail) => !detail.hasAnswer
    );
    const anyUnattempted = unattemptedQuestions.length > 0;

    // Only show dialog if there are actually unattempted questions
    if (anyUnattempted) {
      setPendingAction(() => () => {
        if (listenScreen < playlist.length - 1) {
          setListenScreen((prev) => prev + 1);
        } else if (mode === "practice" && isLastQuestion && onFinish) {
          onFinish();
        } else if (
          mode === "simulation" &&
          isLastModuleInSimulation &&
          isLastQuestionInSimulation &&
          onFinish
        ) {
          onFinish();
        } else {
          onAutoNext?.();
        }
      });
      setShowConfirm(true);
    } else {
      // All attempted: proceed without dialog
      if (listenScreen < playlist.length - 1) {
        setListenScreen((prev) => prev + 1);
      } else if (mode === "practice" && isLastQuestion && onFinish) {
        onFinish();
      } else if (
        mode === "simulation" &&
        isLastModuleInSimulation &&
        isLastQuestionInSimulation &&
        onFinish
      ) {
        onFinish();
      } else {
        onAutoNext?.();
      }
    }
  }, [
    listenScreen,
    playlist.length,
    setListenScreen,
    mode,
    isLastQuestion,
    isLastModuleInSimulation,
    isLastQuestionInSimulation,
    onFinish,
    onAutoNext,
    answers,
    screen,
    headerQuestionIds,
  ]);

  const handleConfirmYes = () => {
    setShowConfirm(false);
    if (pendingAction) pendingAction();
  };

  const handleConfirmNo = () => {
    setShowConfirm(false);
    setPendingAction(null);
  };

  // Handle chip click to navigate to specific question
  const handleChipClick = (questionId) => {
    // For Part B, find the screen with this question and navigate to it
    if (screen?.part === "B") {
      const targetScreenIndex = playlist.findIndex((item) => {
        const id = item.content?.questionId ?? item.content?.question_id;
        return Number(id) === questionId;
      });

      if (targetScreenIndex !== -1) {
        setListenScreen(targetScreenIndex);
        return;
      }
    }

    // For Part A and C, scroll to the question element with professional highlighting
    const element = document.querySelector(
      `[data-question-id="${questionId}"]`
    );
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });

      // Professional highlighting effect
      const originalBackground = element.style.backgroundColor;
      const originalTransition = element.style.transition;
      const originalBoxShadow = element.style.boxShadow;

      // Apply professional highlight
      element.style.transition = "all 0.3s ease-in-out";
      element.style.backgroundColor = "rgba(157, 34, 53, 0.08)";
      element.style.boxShadow =
        "0 0 0 3px rgba(157, 34, 53, 0.2), 0 4px 12px rgba(157, 34, 53, 0.15)";
      element.style.borderRadius = "8px";

      // Pulse effect
      setTimeout(() => {
        element.style.backgroundColor = "rgba(157, 34, 53, 0.15)";
        element.style.boxShadow =
          "0 0 0 4px rgba(157, 34, 53, 0.3), 0 6px 16px rgba(157, 34, 53, 0.2)";
      }, 150);

      // Fade out
      setTimeout(() => {
        element.style.backgroundColor = "rgba(157, 34, 53, 0.05)";
        element.style.boxShadow =
          "0 0 0 2px rgba(157, 34, 53, 0.1), 0 2px 8px rgba(157, 34, 53, 0.1)";
      }, 400);

      // Remove highlight
      setTimeout(() => {
        element.style.backgroundColor = originalBackground;
        element.style.boxShadow = originalBoxShadow;
        element.style.transition = originalTransition;
      }, 2000);
    }
  };

  // Function to format seconds to MM:SS
  const getDisplayTime = () => {
    if (endTime) {
      // API-based timer
      return fmtClock(apiTimeRemaining);
    } else if (mode === "practice") {
      // For practice mode, get time directly from audio element for most accurate display
      const audio = audioRef?.current;
      const currentTime = audio?.currentTime || 0;
      const remaining =
        audioDur > 0 ? Math.max(0, Math.floor(audioDur - currentTime)) : 0;

      // Return the most accurate time based on current audio position
      return fmtClock(remaining);
    } else {
      // Simulation mode
      return fmtClock(audioTimeRemaining);
    }
  };

  const remaining =
    mode === "simulation" ? Math.max(audioDur - audioPos, 0) : null;

  // Only restart audio if audioURL changes
  const lastAudioURL = useRef("");
  useEffect(() => {
    const audio = audioRef?.current;
    if (!audio || !screen) return;
    let src = "";
    if (screen.audioURL) {
      src = screen.audioURL.startsWith("http")
        ? screen.audioURL
        : BASE_URL + screen.audioURL;
    }
    if (src && src !== lastAudioURL.current) {
      audio.pause();
      audio.src = src;
      audio.currentTime = 0;
      if (!instrOpen) {
        audio.play().catch(() => {});
      }
      setAudioPos(0);
      setAudioDur(0);
      lastAudioURL.current = src;
    }
  }, [screen, audioRef, instrOpen]);

  // Track audio progress + auto next
  useEffect(() => {
    const audio = audioRef?.current;
    if (!audio) return;

    const onLoaded = () => {
      // Duration extraction removed - now using backend startTime/endTime
      console.log(
        "Audio loaded, using backend timer instead of audio duration"
      );
    };
    const onTime = () => {
      // Update audio position when timeupdate event fires
      const currentTime = audio.currentTime || 0;
      setAudioPos(currentTime);
    };

    // Audio play/pause handlers - removed audio-based timer logic
    const onPlay = () => {
      setAudioStarted(true);
      setTimerPaused(false);
    };
    const onPause = () => {
      setTimerPaused(true);
    };

    const onEnded = () => {
      // Don't auto-advance if timer has expired - let the timer modal handle it
      if (timerExpired) {
        return;
      }

      // In practice mode, don't auto-advance - let user click Next button
      if (mode === "practice") {
        setTimerPaused(true); // Pause timer when audio ends
        return;
      }

      // Simulation mode: auto-advance as before
      // Check if there's a next extract in the current part
      if (listenScreen < playlist.length - 1) {
        // Move to next extract
        setListenScreen((i) => i + 1);
      } else {
        // Move to next part
        onAutoNext?.();
      }
    };

    audio.addEventListener("loadedmetadata", onLoaded);
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("ended", onEnded);
    return () => {
      audio.removeEventListener("loadedmetadata", onLoaded);
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("ended", onEnded);
    };
  }, [
    audioRef,
    listenScreen,
    playlist.length,
    setListenScreen,
    onAutoNext,
    timerExpired,
    mode,
  ]);

  // Simulation: lock seek/pause - only in simulation mode
  useEffect(() => {
    if (mode !== "simulation") return;
    const audio = audioRef?.current;
    if (!audio) return;

    let last = 0;
    const save = () => {
      last = audio.currentTime;
    };
    const snap = () => {
      audio.currentTime = last;
    };
    const keep = () => {
      if (instrOpen) return;
      setTimeout(() => {
        if (!instrOpen) {
          audio.play().catch(() => {});
        }
      }, 50);
    };

    audio.addEventListener("timeupdate", save);
    audio.addEventListener("seeking", snap);
    audio.addEventListener("pause", keep);

    return () => {
      audio.removeEventListener("timeupdate", save);
      audio.removeEventListener("seeking", snap);
      audio.removeEventListener("pause", keep);
    };
  }, [mode, audioRef, instrOpen]);

  // Page part label (fallback to prop)
  const pagePart = screen?.part || currentPart;
  // Patient name for Part A
  const patientName = useMemo(() => {
    if (screen?.part !== "A") return "";
    return screen.patientName || "";
  }, [screen]);
  // Answer state per chip - filter out invalid IDs (NaN, null, undefined)
  const chipStates = headerQuestionIds
    .filter((qid) => qid != null && !isNaN(qid) && typeof qid === "number")
    .map((qid) => ({
      qid,
      answered: hasAnswer(answers, qid),
    }));

  // Check if current screen is the first extract of any part (to hide back button)
  const isFirstExtractOfPart = useMemo(() => {
    if (!screen) return false;

    // For Part A, check if it's the first Part A screen
    if (screen.part === "A") {
      const firstPartAScreen = playlist.findIndex((item) => item.part === "A");
      return listenScreen === firstPartAScreen;
    }

    // For Part B, check if it's the first Part B screen
    if (screen.part === "B") {
      const firstPartBScreen = playlist.findIndex((item) => item.part === "B");
      return listenScreen === firstPartBScreen;
    }

    // For Part C, check if it's the first Part C screen
    if (screen.part === "C") {
      const firstPartCScreen = playlist.findIndex((item) => item.part === "C");
      return listenScreen === firstPartCScreen;
    }

    return false;
  }, [screen, playlist, listenScreen]);

  if (!screen) {
    return <div className="p-8 text-center">No listening data</div>;
  }

  /* ---------------------------------------------------------------
   Gradient Countdown Ring (SVG)
--------------------------------------------------------------- */
  /* ---------------------------------------------------------------
   Enhanced Gradient Countdown (Ring + Pie + Center Icon)
--------------------------------------------------------------- */
  const EnhancedCountdown = ({
    size = 56,
    stroke = 5,
    progress = 0, // 0..100
    showPie = true, // show inside pie fill
    showRing = true, // show outer progress ring
    idSuffix = "mm2",
  }) => {
    const p = Math.min(100, Math.max(0, progress));
    const finished = p >= 100;

    const r = (size - stroke) / 2;
    const c = 2 * Math.PI * r;
    const dash = c;
    const offset = c * (1 - p / 100);

    // ------- PIE PATH (sector fill) -------
    const angle = (p / 100) * Math.PI * 2;
    const cx = size / 2;
    const cy = size / 2;
    const x = cx + r * Math.cos(-Math.PI / 2 + angle);
    const y = cy + r * Math.sin(-Math.PI / 2 + angle);
    const largeArc = angle > Math.PI ? 1 : 0;
    const piePath =
      p <= 0
        ? ""
        : `
      M ${cx} ${cy}
      L ${cx} ${cy - r}
      A ${r} ${r} 0 ${largeArc} 1 ${x} ${y}
      Z
    `;

    return (
      <Box sx={{ position: "relative", width: size, height: size }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <defs>
            <linearGradient id={`grad-${idSuffix}`} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#9D2235" />
              <stop offset="100%" stopColor="#5d1220" />
            </linearGradient>
            <radialGradient id={`soft-${idSuffix}`} cx="50%" cy="50%" r="60%">
              <stop offset="0%" stopColor="rgba(157,34,53,0.10)" />
              <stop offset="100%" stopColor="rgba(157,34,53,0.0)" />
            </radialGradient>
          </defs>

          {/* Soft background plate for a card-ish look */}
          <circle cx={cx} cy={cy} r={r} fill="url(#soft-mm2)" />

          {/* Track */}
          {showRing && (
            <circle
              cx={cx}
              cy={cy}
              r={r}
              fill="none"
              stroke="rgba(157,34,53,0.18)"
              strokeWidth={stroke}
            />
          )}

          {/* Pie fill from center (sector) */}
          {showPie && p > 0 && (
            <path d={piePath} fill={`url(#grad-${idSuffix})`} />
          )}

          {/* Progress Ring on top */}
          {showRing && (
            <circle
              cx={cx}
              cy={cy}
              r={r}
              fill="none"
              stroke={`url(#grad-${idSuffix})`}
              strokeWidth={stroke}
              strokeLinecap="round"
              strokeDasharray={`${dash} ${dash}`}
              strokeDashoffset={offset}
              transform={`rotate(-90 ${cx} ${cy})`}
            />
          )}

          {/* Subtle glow when finished */}
          {finished && (
            <circle
              cx={cx}
              cy={cy}
              r={r}
              fill="none"
              stroke="#9D2235"
              strokeOpacity="0.25"
              strokeWidth={2}
            />
          )}
        </svg>

        {/* Center icon */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "transform 180ms ease",
            transform: finished ? "scale(1.05)" : "scale(1)",
          }}
        >
          {finished ? (
            <CheckRoundedIcon
              sx={{ fontSize: Math.round(size * 0.34), color: "#7a182a" }}
            />
          ) : (
            <AccessTimeIcon
              sx={{ fontSize: Math.round(size * 0.34), color: "#7a182a" }}
            />
          )}
        </Box>
      </Box>
    );
  };
  const splitClock = (clock = "00:00:00") => {
    const parts = String(clock).split(":");
    const [hh = "00", mm = "00", ss = "00"] =
      parts.length === 3 ? parts : ["00", "00", "00"];
    return { hh, mm, ss };
  };

  /* ---- Countdown math ---- */
  const getRemainingSecs = () => {
    // Priority: extended 10s “Stay Here”
    if (showExtendedTimer) return Math.max(0, extendedCountdown);

    if (endTime) {
      // API-based timer
      return Math.max(0, apiTimeRemaining);
    }

    // Fallback: if no backend timer, return 0
    return 0;
  };

  const getTotalSecs = () => {
    // Priority: extended 10s “Stay Here”
    if (showExtendedTimer) return 10;

    if (endTime) {
      // If we have both start & end, prefer exact total
      if (startTime) {
        const base =
          Math.max(
            1,
            Math.floor(
              (new Date(endTime).getTime() - new Date(startTime).getTime()) /
                1000
            )
          ) || 1;
        return base + timeExtension; // include any extension if you ever add it to API timer
      }
      // Fallback: derive from elapsed + remaining if needed (optional)
      // Without a known start, we can’t know exact total reliably:
      // treat remaining as the denominator substitute to keep a stable UI.
      return Math.max(1, apiTimeRemaining || 1);
    }

    // Fallback
    return Math.max(1, apiTimeRemaining || 1);
  };

  const remainingSecs = getRemainingSecs();
  const totalSecs = getTotalSecs();
  const countdownProgress =
    totalSecs > 0 ? ((totalSecs - remainingSecs) / totalSecs) * 100 : 0;

  return (
    <div className="w-full h-screen flex flex-col bg-gray-50">
      {/* HEADER */}
      <Box
        sx={{
          width: "100%",
          maxWidth: "100%",
          bgcolor: "#F7F7F7",
          px: { xs: 0.5, sm: 0.5, md: 0.5, lg: 0.5 },
          py: { xs: 0.1 },
          position: "sticky",
          top: 0,
          zIndex: 1000,
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Card
          elevation={1}
          sx={{
            width: "100%",
            maxWidth: "100%",
            p: { xs: 0.2, sm: 0.8, md: 1, lg: 1.2 },
            bgcolor: "#fff",
            borderRadius: { xs: 0.8, sm: 1.5, md: 2 },
            boxShadow: "0px 4px 12px rgba(0,0,0,0.05)",
          }}
        >
          <Box
            sx={{
              display: "grid",
              alignItems: "center",
              // Single row layout - all in one line
              gridTemplateAreas: {
                xs: `"left" "center" "right"`, // Stack vertically on mobile
                sm: `"left center right"`,
                md: `"left center right"`,
                lg: `"left center right"`,
              },
              gridTemplateColumns: {
                xs: "1fr", // Single column on mobile
                sm: "1.8fr 1.6fr 1.2fr",
                md: "1.6fr 2fr 1fr",
                lg: "1.8fr 2.2fr 1fr",
              },
              gridTemplateRows: { xs: "auto auto auto", sm: "auto" }, // Multiple rows on mobile
              columnGap: { xs: 0, sm: 0.8, md: 1.2, lg: 1.5 }, // No column gap on mobile
              rowGap: { xs: 0.3, sm: 0 }, // Further reduced row gap on mobile
              minWidth: 0,
              minHeight: { xs: 20, sm: 45, md: 50 }, // Further reduced mobile height
            }}
          >
            {/* LEFT - Listening Info (column) + Audio (responsive layout) */}
            <Box
              sx={{
                gridArea: "left",
                display: "flex",
                flexDirection: { xs: "column", sm: "row" }, // Stack vertically on mobile
                alignItems: { xs: "flex-start", sm: "center" },
                width: "100%",
                gap: { xs: 0.3, sm: 1, md: 1.2, lg: 1.5 }, // Reduced gap on all screens
                py: { xs: 0.1, sm: 0.2 }, // Further reduced vertical padding
              }}
            >
              {/* LEFT COLUMN - text */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  minWidth: 0,
                  flexShrink: 1,
                  pr: { xs: 0, sm: 1 }, // No padding on mobile
                  width: { xs: "100%", sm: "auto" }, // Full width on mobile
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 800,
                    color: "#9D2235",
                    fontSize: { xs: "0.85rem", sm: "1.05rem", md: "1.2rem" },
                    lineHeight: 1.05,
                    whiteSpace: "nowrap",
                  }}
                >
                  Listening Module
                </Typography>

                <Box sx={{ mt: 0.5 }}>
                  <Typography
                    component="span"
                    sx={{
                      display: "inline-block",
                      fontWeight: 800,
                      color: "#222",
                      fontSize: { xs: "0.8rem", sm: "0.95rem", md: "1.05rem" },
                      lineHeight: 1.1,
                      letterSpacing: 0.2,
                      textTransform: "none",
                    }}
                  >
                    {screen?.extractName ? (
                      <Box
                        component="span"
                        sx={{
                          bgcolor: "rgba(0,0,0,0.06)",
                          px: 1,
                          py: 0.25,
                          borderRadius: 1,
                          fontWeight: 800,
                          color: "#1f2937",
                        }}
                      >
                        {screen.extractName}
                      </Box>
                    ) : (
                      `Part (${pagePart}) Extract ${
                        partIdx + 1
                      } of ${partTotal}`
                    )}
                  </Typography>
                </Box>
                {/* Summary / group heading (questionMainHeading, range, instructions) */}
                {partAFiltered.summary && (
                  <Box
                    sx={{
                      mt: 1,
                      pl: 0.5,
                      minWidth: 180,
                      maxWidth: { xs: 220, sm: 360 },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 0.25,
                        color: "#9D2235",
                        fontWeight: 700,
                        fontSize: 14,
                        lineHeight: 1.2,
                      }}
                    >
                      <ArrowRightIcon sx={{ fontSize: 18, mr: 0.5 }} />
                      {partAFiltered.summary.heading}
                    </Box>
                    {partAFiltered.summary.lines?.length ? (
                      <Box
                        component="ul"
                        sx={{
                          m: 0,
                          pl: 2.5,
                          color: "#1f2937",
                          fontSize: 13,
                          lineHeight: 1.3,
                          "& li": { mb: 0.25, listStyle: "disc" },
                        }}
                      >
                        {partAFiltered.summary.lines.map((txt, i) => (
                          <li key={i}>{txt}</li>
                        ))}
                      </Box>
                    ) : null}
                  </Box>
                )}
              </Box>

              {/* RIGHT SIDE - Mobile Responsive Audio Player */}
              <Box
                sx={{
                  ml: { xs: 0, sm: "auto" }, // No auto margin on mobile
                  display: "flex",
                  alignItems: "center",
                  justifyContent: { xs: "flex-start", sm: "flex-end" },
                  flexShrink: 0,
                  width: { xs: "100%", sm: "auto" }, // Full width on mobile
                  maxWidth: { xs: "100%", sm: "800px" }, // Increased max width to match audio element
                }}
              >
                <audio
                  ref={audioRef}
                  data-audio-type="unified"
                  controls
                  controlsList="nodownload noplaybackrate noremoteplaybook"
                  onContextMenu={(e) => e.preventDefault()}
                  style={{
                    width: "100%",
                    maxWidth: "800px", // Significantly increased max width
                    minWidth: "250px", // Increased min width for better mobile experience
                    height: "40px", // Slightly increased height for better visibility
                    outline: "none",
                    pointerEvents: mode === "simulation" ? "none" : "auto",
                    accentColor: "#9D2235",
                    borderRadius: "10px",

                    padding: "3px 6px", // Increased padding for better controls
                    margin: 0,
                    WebkitAppearance: "none",
                    appearance: "none",
                    transition: "all 0.2s",
                  }}
                />
              </Box>
            </Box>

            {/* CENTER -  Chips */}
            <Box
              sx={{
                gridArea: "center",
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: "center",
                gap: { xs: 0.2, sm: 0.8, md: 1.2, lg: 1.5 },
                minWidth: 0,
                justifyContent: "center",
                py: { xs: 0.1, sm: 0.3, md: 0.4 },
              }}
            >
              {/* Chips */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  px: 0,
                  py: 0,
                  minWidth: { xs: "100%", sm: "auto" },
                  justifyContent: { xs: "center", sm: "flex-start" },
                }}
              >
                <Box
                  sx={{
                    display: "inline-flex",
                    gap: { xs: 0.2, sm: 0.3, md: 0.4, lg: 0.5 },
                    flexWrap: "wrap",
                    justifyContent: "center",
                  }}
                >
                  {chipStates.map(({ qid, answered }) => (
                    <Box
                      key={qid}
                      onClick={() => handleChipClick(qid)}
                      sx={{
                        width: { xs: 18, sm: 22, md: 26, lg: 30 },
                        height: { xs: 18, sm: 22, md: 26, lg: 30 },
                        borderRadius: "50%",
                        border: "1.5px solid #9D2235",
                        bgcolor: answered ? "#9D2235" : "transparent",
                        color: answered ? "#fff" : "#9D2235",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 700,
                        fontSize: {
                          xs: "0.6rem",
                          sm: "0.7rem",
                          md: "0.8rem",
                          lg: "0.9rem",
                        },
                        cursor: "pointer",
                        transition: "background 0.15s",
                        "&:hover": {
                          bgcolor: answered
                            ? "#7a182a"
                            : "rgba(157,34,53,0.08)",
                        },
                        "&:active": {},
                        flex: "0 0 auto",
                      }}
                    >
                      {qid}
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>

            {/* RIGHT - Circle Clock, Timer and Notes */}
            <Box
              sx={{
                gridArea: "right",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: { xs: 0.3, sm: 0.6, md: 0.8 },
              }}
            >
              {/* Circle Clock - Above everything */}
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <EnhancedCountdown
                  size={20}
                  stroke={2}
                  progress={countdownProgress}
                  showPie
                  showRing
                  idSuffix="headerTime"
                />
              </Box>

              {/* Timer and Notes Row - Below circle */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: { xs: 0.4, sm: 0.8, md: 1 },
                }}
              >
                {/* Timer */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: { xs: 0.5, sm: 0.6, md: 0.7 },
                    px: { xs: 0.8, sm: 1, md: 1.2 },
                    py: { xs: 0.4, sm: 0.5, md: 0.6 },
                    borderRadius: { xs: 1.5, sm: 2, md: 2.5 },
                    minWidth: { xs: 70, sm: 90, md: 110 },
                    transition: "all 0.2s ease-in-out",
                  }}
                >
                  <AccessTimeIcon
                    sx={{
                      fontSize: { xs: 16, sm: 20, md: 24 },
                      color: "#9D2235",
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: { xs: "0.8rem", sm: "1rem", md: "1.2rem" },
                      fontWeight: 800,
                      color: "#9D2235",
                      fontFamily: "'Roboto Mono', 'Courier New', monospace",
                      letterSpacing: { xs: 0.4, sm: 0.6, md: 0.8 },
                      lineHeight: 1,
                    }}
                  >
                    {(() => {
                      const text = showExtendedTimer
                        ? `00:00:${pad2(extendedCountdown)}`
                        : endTime
                        ? getDisplayTime()
                        : mode === "practice"
                        ? getDisplayTime()
                        : mode === "simulation"
                        ? getDisplayTime()
                        : timer === "no-limit"
                        ? fmtClock(totalSecs)
                        : timer || "00:00:00";
                      return text;
                    })()}
                  </Typography>
                </Box>

                {/* Notes Icon */}
                <Badge
                  badgeContent={notes?.length || 0}
                  sx={{
                    "& .MuiBadge-badge": {
                      bgcolor: "#fff",
                      color: "#9D2235",
                      fontWeight: "bold",
                      fontSize: { xs: "0.35rem", sm: "0.45rem", md: "0.5rem" },
                      minWidth: { xs: 6, sm: 10, md: 12 },
                      height: { xs: 6, sm: 10, md: 12 },
                      boxShadow: "0 2px 4px rgba(157,34,53,0.2)",
                    },
                  }}
                >
                  <IconButton
                    onClick={() => setNotesModalOpen && setNotesModalOpen(true)}
                    sx={{
                      background:
                        "linear-gradient(135deg, #9D2235 0%, #7a182a 100%)",
                      color: "#fff",
                      border: "1px solid rgba(255,255,255,0.1)",
                      boxShadow:
                        "0 4px 12px rgba(157,34,53,0.25), 0 2px 4px rgba(157,34,53,0.15)",
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, #7a182a 0%, #5d1220 100%)",
                        transform: "translateY(-1px)",
                        boxShadow:
                          "0 6px 16px rgba(157,34,53,0.3), 0 4px 8px rgba(157,34,53,0.2)",
                      },
                      width: { xs: 18, sm: 28, md: 32 },
                      height: { xs: 18, sm: 28, md: 32 },
                    }}
                  >
                    <NoteIcon
                      sx={{
                        fontSize: { xs: "0.6rem", sm: "0.9rem", md: "1rem" },
                        filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.3))",
                      }}
                    />
                  </IconButton>
                </Badge>
              </Box>
            </Box>
          </Box>
        </Card>
      </Box>
      {/* Body Section */}
      <Box
        sx={{
          flex: 1,
          width: "100%",
          overflowY: "auto",
          bgcolor: "#F7F7F7",
          px: { xs: 1, sm: 1 },
          py: { xs: 1, sm: 1 },
          pb: 0,
        }}
      >
        {/* Content Area */}
        <TextHighlighterWrapper onAddNote={onAddNote}>
          <Box sx={{ width: "100%" }}>
            {screen.type === "noteCompletion" && (
              <Card
                elevation={0}
                sx={{
                  p: { xs: 1, sm: 0 },
                  borderRadius: 2,
                  bgcolor: "#F7F7F7",
                  width: "100%",
                }}
              >
                <MockNoteCompletion
                  extractName={screen.extractName}
                  noteCompletionData={{ sections: partAFiltered.sections }}
                  answers={answers}
                  handleAnswerChange={(id, v) => onAnswerChange(id, v)}
                  dataQuestionIds={partAFiltered.questionIds}
                  instructions={screen.instructions || ""}
                />
              </Card>
            )}

            {screen.type === "composite" && (
              <Box sx={{ width: "100%" }}>
                {/* Render an ordered list of items. Group consecutive items by instruction and show instruction before each group. */}
                {(() => {
                  const items = screen.content?.items || [];
                  const out = [];
                  let noteBuffer = [];
                  let currentInstruction = null;

                  const flushNotes = () => {
                    if (!noteBuffer.length) return;
                    // group buffered notes by heading
                    const byHeading = new Map();
                    // Extract questionMainHeading from first note item
                    const questionMainHeading =
                      noteBuffer[0]?.questionMainHeading || "";

                    noteBuffer.forEach((n) => {
                      const h = normalizeHeading(n.heading || "");
                      if (!byHeading.has(h)) byHeading.set(h, []);
                      byHeading.get(h).push({
                        questionId: n.questionId,
                        text: n.question,
                        hasBlank: n.questionId != null,
                      });
                    });
                    const sections = Array.from(byHeading.entries()).map(
                      ([heading, notes]) => ({ heading, notes })
                    );
                    out.push(
                      <Card
                        key={`notes-${out.length}`}
                        elevation={0}
                        sx={{
                          p: { xs: 1, sm: 0 },
                          borderRadius: 2,
                          bgcolor: "#F7F7F7",
                          width: "100%",
                        }}
                      >
                        <MockNoteCompletion
                          extractName={screen.extractName}
                          noteCompletionData={{ sections }}
                          answers={answers}
                          handleAnswerChange={(id, v) => onAnswerChange(id, v)}
                          dataQuestionIds={screen.questionIds || []}
                          questionMainHeading={questionMainHeading}
                        />
                      </Card>
                    );
                    noteBuffer = [];
                  };

                  items.forEach((item, idx) => {
                    // Check if instruction changed - if so, show new instruction block
                    const itemInstruction = item.instructions || "";
                    if (
                      itemInstruction &&
                      itemInstruction !== currentInstruction
                    ) {
                      // Flush any pending notes before showing new instruction
                      flushNotes();

                      // Show instruction for this new group
                      currentInstruction = itemInstruction;
                      out.push(
                        <Paper
                          key={`instruction-${idx}`}
                          elevation={0}
                          sx={{
                            mb: 3,
                            mt: out.length > 0 ? 3 : 0,
                            p: 2,
                            border: `1px solid ${border}`,
                            borderRadius: 1.5,
                            bgcolor: "#fff",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 1,
                            }}
                          >
                            <InfoOutlinedIcon
                              sx={{ fontSize: 28, color: "#9D2235", mr: 1 }}
                            />
                            <Typography
                              variant="h5"
                              sx={{ fontWeight: 700, color: "#9D2235" }}
                            >
                              Instruction
                            </Typography>
                          </Box>
                          <HtmlRenderer
                            content={itemInstruction}
                            component="div"
                            sx={{
                              lineHeight: 1.5,
                              userSelect: "text",
                              cursor: "text",
                              fontSize: "0.875rem",
                            }}
                          />
                        </Paper>
                      );
                    }

                    if (item.type === "note") {
                      noteBuffer.push(item);
                      return;
                    }
                    // on encountering non-note, first flush any notes
                    flushNotes();

                    if (
                      item.type === "mcq_single" ||
                      item.type === "mcq_mutiple"
                    ) {
                      out.push(
                        <Box
                          key={`mcq-${idx}`}
                          data-question-id={item.questionId}
                          sx={{ mt: 2 }}
                        >
                          <MockMcq
                            mcqData={item}
                            selectedValue={answers[item.questionId] || ""}
                            handleAnswerChange={(id, v) =>
                              onAnswerChange(id, v)
                            }
                          />
                        </Box>
                      );
                      return;
                    }

                    // Fallback rendering for unknown types - show basic question text
                    out.push(
                      <Paper
                        key={`other-${idx}`}
                        elevation={0}
                        sx={{
                          p: 2,
                          mt: 2,
                          border: `1px solid ${border}`,
                          borderRadius: 1.5,
                        }}
                      >
                        <HtmlRenderer content={item.question} component="div" />
                      </Paper>
                    );
                  });

                  // flush any remaining notes
                  flushNotes();
                  return out;
                })()}
              </Box>
            )}

            {screen.type === "mcqSingle" && (
              <Box
                sx={{ width: "100%" }}
                data-question-id={
                  screen.content.questionId ?? screen.content.question_id
                }
              >
                <MockMcq
                  mcqData={screen.content}
                  selectedValue={
                    answers[
                      screen.content.questionId ?? screen.content.question_id
                    ] || ""
                  }
                  handleAnswerChange={(id, v) => onAnswerChange(id, v)}
                  instructions={screen.instructions}
                  partKey="partB"
                />
              </Box>
            )}

            {screen.type === "mcqGroup" && (
              <Box sx={{ width: "100%" }}>
                {screen.instructions && (
                  <Paper
                    elevation={0}
                    sx={{
                      mb: 3,
                      p: 2,
                      border: `1px solid ${border}`,
                      borderRadius: 1.5,
                      bgcolor: "#fff",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <InfoOutlinedIcon
                        sx={{ fontSize: 28, color: "#9D2235", mr: 1 }}
                      />
                      <Typography
                        variant="h5"
                        sx={{ fontWeight: 700, color: "#9D2235" }}
                      >
                        Instruction
                      </Typography>
                    </Box>
                    <HtmlRenderer
                      content={screen.instructions}
                      component="div"
                      sx={{
                        lineHeight: 1.5,
                        userSelect: "text", // Enable text selection
                        cursor: "text", // Show text cursor
                        fontSize: "0.875rem", // Match Typography body2
                      }}
                    />
                  </Paper>
                )}

                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                >
                  {screen.contents.map((mcq, idx) => (
                    <Box key={idx} data-question-id={mcq.questionId}>
                      <MockMcq
                        mcqData={mcq}
                        selectedValue={answers[mcq.questionId] || ""}
                        handleAnswerChange={(id, v) => onAnswerChange(id, v)}
                        partKey="partC"
                      />
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </TextHighlighterWrapper>
      </Box>{" "}
      {/* FOOTER */}
      <Box
        sx={{
          bgcolor: bgPaper,
          borderTop: `1px solid ${divider}`,
          px: { xs: 2, sm: 4 },
          py: 2,
          position: "sticky",
          zIndex: 10,
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {/* Back Button - only show in practice mode, not on first screen, and not on first extract of any part */}
        {mode === "practice" &&
          (listenScreen > 0 || onBack) &&
          !isFirstExtractOfPart && (
            <Button
              variant="outlined"
              onClick={() => {
                if (listenScreen > 0) {
                  setListenScreen((prev) => prev - 1);
                } else if (onBack) {
                  onBack();
                }
              }}
              sx={{
                minWidth: 100,
                textTransform: "none",
                borderColor: "#9D2235",
                color: "#9D2235",
                py: 1,
                borderRadius: 3,
                "&:hover": {
                  borderColor: "#9D2235",
                  backgroundColor: "rgba(157, 34, 53, 0.04)",
                },
              }}
            >
              Back
            </Button>
          )}
        {!(
          mode === "practice" &&
          (listenScreen > 0 || onBack) &&
          !isFirstExtractOfPart
        ) && <Box />}

        {/* Continue/Next/Finish Button */}
        <Button
          variant="contained"
          disableElevation
          onClick={handleNextClick}
          sx={{
            minWidth: 140,
            textTransform: "none",
            bgcolor: "#9D2235",
            py: 1,
            borderRadius: 3,
          }}
        >
          {listenScreen < playlist.length - 1
            ? "Continue"
            : mode === "practice" && isLastQuestion
            ? "Finish"
            : mode === "simulation" &&
              isLastModuleInSimulation &&
              isLastQuestionInSimulation
            ? "Finish"
            : screen.part === "C"
            ? "Next Section"
            : "Next Part"}
        </Button>
      </Box>
      {/* TIMER EXPIRED MODAL */}
      <Dialog open={timerExpired && !expiryHandled}>
        <DialogContent>
          <Typography variant="h6" textAlign="center" gutterBottom>
            Time's up!
          </Typography>
          <Typography textAlign="center" sx={{ mb: 2 }}>
            The test will be submitted in {expiryCountdown} seconds.
          </Typography>
          <Box textAlign="center" sx={{ mb: 2 }}>
            <Typography
              variant="h1"
              sx={{
                fontWeight: 800,
                fontSize: { xs: 48, sm: 72 },
                color: "#9D2235",
              }}
            >
              {expiryCountdown}
            </Typography>
            <Typography variant="subtitle1" sx={{ mt: -1 }}>
              seconds
            </Typography>
          </Box>
          <Box
            textAlign="center"
            sx={{ display: "flex", gap: 2, justifyContent: "center" }}
          >
            {!stayHereUsed && (
              <Button
                variant="outlined"
                onClick={handleStayHere}
                sx={{
                  borderColor: "#9D2235",
                  color: "#9D2235",
                  mt: 1,
                  "&:hover": {
                    borderColor: "#7a1a2a",
                    backgroundColor: "rgba(157, 34, 53, 0.04)",
                  },
                }}
              >
                Stay Here
              </Button>
            )}
            <Button
              variant="contained"
              color="secondary"
              onClick={handleTimerContinue}
              sx={{ bgcolor: "#9D2235", mt: 1 }}
            >
              Continue
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
      {/* CONFIRM NAVIGATION MODAL */}
      <Dialog open={showConfirm} onClose={handleConfirmNo}>
        <DialogContent>
          <DialogTitle sx={{ textAlign: "center", pt: 3 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Warning color="warning" sx={{ fontSize: 50 }} />
              <Typography variant="h6" fontWeight="bold">
                Unattempted Question – Confirm Section Completion
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 2, pb: 3 }}>
            <Typography variant="body1" paragraph>
              Are you sure you want to move to the next section without
              completing question?
            </Typography>
            <Typography variant="body1" fontWeight="bold">
              Are you sure you want to finish this section?
            </Typography>
          </DialogContent>
          <Box mt={2} display="flex" justifyContent="center" gap={2}>
            <Button variant="outlined" onClick={handleConfirmNo}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmYes}
              sx={{ bgcolor: "#9D2235" }}
            >
              Yes, Continue
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
      {/* Notes Modal */}
      <NotesModal
        open={notesModalOpen}
        onClose={() => setNotesModalOpen && setNotesModalOpen(false)}
        notes={notes}
      />
    </div>
  );
}
